/**
 * User Signals API — 隐式用户反馈信号采集
 * Fire-and-forget：不 await，不阻塞用户操作
 */
import { API_BASE_URL, getAuthToken } from './client';

export type SignalType =
  | 'prompt_correct'
  | 'variant_select'
  | 'regenerate'
  | 'node_delete'
  | 'used_as_reference'   // AI 生成结果被拿来当 reference 继续生成
  | 'result_downloaded'   // 下载/导出
  | 'result_saved'        // 存入 Assets / 收藏
  | 'trend_interaction'    // 趋势卡片曝光/点击/模板使用
  | 'marketing_attribution' // Web marketing attribution event
  | 'feature_used'        // 画布功能使用（context.feature_name 标识具体功能）
  | 'journey';            // 页面/会话旅程元数据（不采集输入内容）

export interface PromptCorrectContext {
  node_id: string;
  original_prompt: string;
  edited_prompt: string;
  capability: string;
}

/**
 * 发送用户行为信号（fire-and-forget）
 * 失败静默，绝不影响用户操作
 */
export function trackSignal(
  signalType: SignalType,
  context: Record<string, unknown>,
  projectId?: string,
  canSend: () => boolean = () => true,
): void {
  try {
    // 必须在读取当前 token 前验证捕获的 user/auth generation，防止旧流程借用新账号 token。
    if (!canSend()) return;
    const token = getAuthToken();
    if (!token) return;

    const body = JSON.stringify({
      signal_type: signalType,
      project_id: projectId,
      context,
    });

    // fire-and-forget fetch
    fetch(`${API_BASE_URL}/signals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      keepalive: true, // 允许页面关闭后继续发送
    }).catch(() => {
      // 静默失败
    });
  } catch {
    // 静默失败
  }
}

/**
 * Journey 专用可确认发送：仅 HTTP ok 且 envelope success:true 才视为已确认。
 * 用于 session_started / daily_active 的 durable 重试语义；普通事件仍走 trackSignal。
 * 绝不 reject：任何失败都 resolve false，由调用方保留状态稍后重试。
 */
export async function trackSignalWithAck(
  signalType: SignalType,
  context: Record<string, unknown>,
  projectId?: string,
  canSend: () => boolean = () => true,
): Promise<boolean> {
  try {
    if (!canSend()) return false;
    const token = getAuthToken();
    if (!token) return false;

    const response = await fetch(`${API_BASE_URL}/signals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        signal_type: signalType,
        project_id: projectId,
        context,
      }),
      keepalive: true,
    });
    if (!response.ok) return false;
    const envelope = (await response.json()) as { success?: boolean } | null;
    return envelope?.success === true;
  } catch {
    return false;
  }
}
